import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';

// Material
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';

// Shared
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Sort } from '@angular/material/sort';
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import { Router } from '@angular/router';
import { finalize, Subject, take } from 'rxjs';
import { GestioneCrawlingService } from '../../services/gestione-crawling.service';
import { NotificationService } from '../../services/notification.service';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { SearchBarComponent } from '../../shared/searchbar/searchbar.component';
import { SharedTableComponent } from '../../shared/shared-table/shared-table.component';
import { CrawlingWebModalComponent } from '../specific-modals/crawling-web-modal/crawling-web-modal.component';
import { CrawlingLogComponent } from './crawling-log/crawling-log.component';
import { CrawlingOperationsComponent } from "./crawling-operations/crawling-operations.component";

type JobStatus = 'job stopped' | 'job running' | 'job paused';

@Component({
  selector: 'app-crawling-web',
  standalone: true,
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    SharedTableComponent,
    SearchBarComponent,
    MatProgressSpinnerModule,
    CommonModule,
    LoadingComponent,
    MatTabGroup,
    MatTab,
    CrawlingLogComponent,
    CrawlingOperationsComponent
],
  templateUrl: './crawling-web.component.html',
  styleUrls: ['./crawling-web.component.scss'],
})
export class CrawlingWebComponent implements OnInit, OnDestroy {
  
  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly crawlingWebService: GestioneCrawlingService,
    private readonly notificationService: NotificationService
  ) {}

  displayedColumns: string[] = ['name', 'status'];
  columnLabels: Record<string, string> = {
    name: 'Nome',
    status: 'Status',
  };

  hasErrorDraft = false;
  isLoadingDraft = false;

  hasErrorRunnable = false;
  isLoadingRunnable = false;

  isLoadingFullScreen = false;
  jobs: any = [];

  jobsListLoaded$ = new Subject<void>(); //fine caricamento lista
  
  //Mappe: jobId --> stato iniziale
  pendingJobs = new Map<number, string>();
  pendingStopAll = new Map<number, string>();

  inActivityStates = new Set(['RUNNING']);

  // Stato query e paginazione RUNNABLE JOBS
  runnableSortBy = 'name:asc';
  runnableSortField = '';
  runnableSortDirection = '';
  runnablePageIndex = 0;
  runnablePageSize = 5;
  runnableTotalItems = 0;
  runnableFilter = {
    search: '',
    status: ''
  };

  // Stato query e paginazione DRAFT JOBS
  draftSortBy = 'name:asc';
  draftSortField = '';
  draftSortDirection = '';
  draftPageIndex = 0;
  draftPageSize = 5;
  totalDraftItems = 0;
  draftFilter = {
    search: '',
    status: 'DRAFT'
  };

  dataSourceCrawlingWeb = new MatTableDataSource<any>();
  datasourceDraft = new MatTableDataSource<any>();

  ngOnInit(): void {
    // primo load senza filtri per i runnable jobs
    this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.runnableFilter);
    //load con filtro draft per i draft jobs
    this.loadDraftJobScheduleData(this.draftPageIndex, this.draftPageSize, this.draftSortBy, this.draftFilter);
    
  }

  private currentRunnableFilter() {
    const q = this.runnableFilter.search?.trim() ?? '';
    return q ? { search: q } : {};
  }

  private currentDraftFilter() {
    const q = this.draftFilter.search?.trim() ?? '';
    return q ? { search: q, status: 'DRAFT' } : {status: 'DRAFT'};
  }

  loadRunnableJobScheduleData(
    pageIndex: number,
    pageSize: number,
    sortBy: string,
    jobScheduleFilter: any
  ): void {
    this.isLoadingRunnable = true;
    this.hasErrorRunnable = false;
    this.crawlingWebService.getJobScheduleList(pageIndex, pageSize, sortBy, jobScheduleFilter).pipe(finalize(() => { 
      this.isLoadingRunnable = false;
      this.jobsListLoaded$.next(); 
    })).subscribe({
      next: (response) => {
        this.jobs = response?.content ?? [];
        this.dataSourceCrawlingWeb.data = this.jobs;
        this.runnableTotalItems = response?.totalElements ?? 0;
        this.runnablePageIndex = (response?.number ?? pageIndex) ?? 0;
        this.runnablePageSize  = (response?.size ?? pageSize) ?? this.runnablePageSize;

        //confronto con lo stato iniziale --> se è cambiato tolgo il pending
        this.jobs.forEach((job: any) => {
          const pendingJobStatus = this.pendingJobs.get(job.id);
          
          if(pendingJobStatus && job.status !== pendingJobStatus){
            this.pendingJobs.delete(job.id);
          }
          const pendingStopAllStatus = this.pendingStopAll.get(job.id);
          
          if(pendingStopAllStatus && job.status !== pendingStopAllStatus){
            this.pendingStopAll.delete(job.id);
          }

        })
        
      },
      error: (err) => {
        console.log('errore nel recupero dei dati', err);
        this.jobs = [];
        this.dataSourceCrawlingWeb.data = [];
        this.runnableTotalItems = 0;
        this.hasErrorRunnable = true;
      },
    });
  }

  loadDraftJobScheduleData(
    pageIndex: number,
    pageSize: number,
    sortBy: string,
    jobScheduleFilter: any
  ): void {
    this.isLoadingDraft = true;
    this.hasErrorDraft = false;
    this.crawlingWebService.getJobScheduleList(pageIndex, pageSize, sortBy, jobScheduleFilter).pipe(finalize(() => { 
      this.isLoadingDraft = false;
      this.jobsListLoaded$.next(); 
    })).subscribe({
      next: (response) => {
        this.jobs = response?.content ?? [];
        this.datasourceDraft.data = this.jobs;
        this.totalDraftItems = response?.totalElements ?? 0;
        this.draftPageIndex = (response?.number ?? pageIndex) ?? 0;
        this.draftPageSize  = (response?.size ?? pageSize) ?? this.draftPageSize;
      },
      error: (err) => {
        console.log('errore nel recupero dei dati', err);
        this.jobs = [];
        this.datasourceDraft.data = [];
        this.totalDraftItems = 0;
        this.hasErrorDraft = true;
      },
    });
  }

// ------- RUNNABLE FUNCTIONS -------
  // Filtro
  onRunnableFilterChanged(value: any) {
    this.runnableFilter.search = value?.nome ?? '';
    this.runnablePageIndex = 0;
    this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, 'name:asc', this.currentRunnableFilter());
  }

  // Ordinamento
  onRunnableSortChanged(sort: Sort) {
    this.runnableSortField = sort.active;
    this.runnableSortDirection = sort.direction || 'asc';
    this.runnableSortBy = `${this.runnableSortField}:${this.runnableSortDirection}`;
    this.runnablePageIndex = 0;
    this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
  }

  // Cambio pagina / page size (server-side)
  onRunnablePageChanged(ev: PageEvent) {
    this.runnablePageIndex = ev.pageIndex;
    this.runnablePageSize  = ev.pageSize;
    this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
  }

  // ------- DRAFT FUNCTIONS -------
  // Filtro
  onDraftFilterChanged(value: any) {
    this.draftFilter.search = value?.nome ?? '';
    this.draftPageIndex = 0;
    this.loadDraftJobScheduleData(this.draftPageIndex, this.draftPageSize, 'name:asc', this.currentDraftFilter());
  }

  // Ordinamento
  onDraftSortChanged(sort: Sort) {
    this.draftSortField = sort.active;
    this.draftSortDirection = sort.direction || 'asc';
    this.draftSortBy = `${this.draftSortField}:${this.draftSortDirection}`;
    this.draftPageIndex = 0;
    this.loadDraftJobScheduleData(this.draftPageIndex, this.draftPageSize, this.draftSortBy, this.currentDraftFilter());
  }

  // Cambio pagina / page size (server-side)
  onDraftPageChanged(ev: PageEvent) {
    this.draftPageIndex = ev.pageIndex;
    this.draftPageSize  = ev.pageSize;
    this.loadDraftJobScheduleData(this.draftPageIndex, this.draftPageSize, this.draftSortBy, this.currentDraftFilter());
  }

  goToDetail(row: any) {
    localStorage.setItem('selectedJobScheduleRow', JSON.stringify(row));
    this.router.navigate(['/crawling-web/detail', row.id, encodeURIComponent(row.name)]);
  }

  goToScheduleDetail(element: any) {
    localStorage.setItem('selectedJobScheduleRow', JSON.stringify(element));
    this.router.navigate(
      ['/crawling-web/detail', element.id, encodeURIComponent(element.name)],
      {queryParams: {tab: 'schedulazione'}}
    )
  }

  onToggleJobActivation(event: { data: any, action: 'activate' | 'deactivate' }){
    console.log('toggle activation', event.action, 'job su id: ', event.data.id);

    this.isLoadingFullScreen = true;    

    this.crawlingWebService.changeJobScheduleState(event.data.id).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
            this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
            this.jobsListLoaded$.pipe(take(1)).subscribe(() => {
              this.notificationService.stateSuccess(event.data.name);
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err); 
            this.notificationService.stateError(event.data.name);
          }
        });
  }

  private transformPayload(payload: any) {
    const {selectDay, selectHour, selectMinute, ...rest} = payload;

    return {
      ...rest,
      trigger:{
        cronExpression: ''
      }
    }
  }

  runOrStopJob(event: {data: any; type: 'start'| 'stop'}) {
    
    if(this.pendingJobs.has(event.data.id)) {
      console.log('pending (job già in corso)');
      return;
    }
    
    this.pendingJobs.set(event.data.id, event.data.status);

    console.log('run or stop', event.data.status, event.type);
    if(event.type === 'start'){
      this.crawlingWebService.runJobSchedule(event.data.id).subscribe({
          next: () => {
            this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
          },
          error: (err) => {
            console.log('errore nella richiesta', err); 
            this.pendingJobs.delete(event.data.id);
          }
        });
    } else if(event.type === 'stop'){
      this.crawlingWebService.stopJobSchedule(event.data.id).subscribe({
          next: () => {
            this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.pendingJobs.delete(event.data.id); 
          }
        });
    }
  }

  isJobPending(jobId: number): boolean {
    return this.pendingJobs.has(jobId) || this.pendingStopAll.has(jobId);
  }

  isAnyJobPending(): boolean {
    return this.pendingJobs.size > 0 || this.pendingStopAll.size > 0;
  }

  //se job in attività (running) non posso modificarlo o eliminarlo
  shouldDisableActionsOnRow(job: any): boolean {
    return this.inActivityStates.has(job.status);
  }

  stopOrRefreshAllJobs(event: any){
     console.log('stop or refresh all', event);
     if(event === 'stopAll'){
      
      if(this.pendingStopAll.size > 0) {
        console.log('pending (stop all già in corso)');
        return;
      }

      //filtro per i job che sono stoppabili e li aggiungo ai pendingStopAll
      const stoppableStatuses = ['RUNNING'];
      this.jobs.forEach((job: any) => {
        if(stoppableStatuses.includes(job.status)) {
          this.pendingStopAll.set(job.id, job.status);
        }
      });

      //se non ci sono job stoppabili, evito la chiamata
      if(this.pendingStopAll.size === 0) {
        console.log('Nessun job da stoppare');
        return;
      }

      this.crawlingWebService.stopAllJObs().subscribe({
          next: () => {
            this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
          },
          error: (err) => {
            console.log('errore nella richiesta', err); 
            this.pendingStopAll.clear();
          }
        });
    } else if(event === 'refresh'){
        this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
    }

  }



  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete', nameTable?: string }) {
    const dialogRef = this.dialog.open(CrawlingWebModalComponent, {
      width: '400px',
      data: {
        action: event.type,
        payload: event.data,
        nameTable: event.nameTable,
        selectOptions: event.data.selectOptions
      },
    });
    console.log('tabella principale', event.nameTable);
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.isLoadingFullScreen = true;

      const rawPayload = result.payload;
      const documentFile = result.document;

      if (event.type === 'add') {
        console.log('result add (raw)', rawPayload);
        if(rawPayload.extraInputUrlsArray) {
          rawPayload.urls = rawPayload.extraInputUrlsArray.map((item:any)=> item.url);
          delete rawPayload.extraInputUrlsArray;
        }
        const defPayload = this.transformPayload(rawPayload);
        console.log('payload add def', defPayload);
        
        const formDataToSubmit = new FormData();

        const payloadString = JSON.stringify(defPayload);

        const jsonBlob = new Blob([payloadString], {type: 'application/json'});

        formDataToSubmit.append('jobSchedule', jsonBlob);
        
        if(documentFile){
          formDataToSubmit.append('document', documentFile);
        }

        this.crawlingWebService.addNewJobSchedule(formDataToSubmit).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
            this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
            this.loadDraftJobScheduleData(this.draftPageIndex, this.draftPageSize, this.draftSortBy, this.currentDraftFilter());
            this.jobsListLoaded$.pipe(take(1)).subscribe(() => {
              this.notificationService.entitySuccess('add', 'Job Schedule');
            })
            
          },
          error: (err) => {
            console.log('errore nella richiesta', err)
            this.notificationService.entityError('add', 'Job Schedule');
          }
        });

      } else if (event.type === 'delete') {
        console.log('result delete', rawPayload);
        this.crawlingWebService.deleteJobSchedule(rawPayload.id).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
            if (this.runnablePageIndex > 0 && this.dataSourceCrawlingWeb.data.length === 1) {
              this.runnablePageIndex = this.runnablePageIndex - 1;
            }
            if (this.draftPageIndex > 0 && this.datasourceDraft.data.length === 1) {
              this.draftPageIndex = this.draftPageIndex - 1;
            }
            this.loadRunnableJobScheduleData(this.runnablePageIndex, this.runnablePageSize, this.runnableSortBy, this.currentRunnableFilter());
            this.loadDraftJobScheduleData(this.draftPageIndex, this.draftPageSize, this.draftSortBy, this.currentDraftFilter());
            this.jobsListLoaded$.pipe(take(1)).subscribe(() => {
              this.notificationService.entitySuccess('delete', 'Job Schedule');
            })
            
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('delete', 'Job Schedule');
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.jobsListLoaded$.complete();
  }
}